import { useMemo } from 'react';
import {
  SidePanelTab,
  SidePanelTabContent,
  SidePanelTabList,
  SidePanelTabs,
} from '../../components/SidePanel';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MACHINE_TILE_IMAGE } from '../../demo/demoMode';
import { machineSubtitle } from '../../components/machines/machineSubtitle';
import { useMachineDetailTabs } from '../../contexts/machineDetailTabs';
import { Machine } from '../../gql/generated';
import { machineDataTabs } from './machineDataTabs';
import { MACHINE_OWN_TABS } from './machineOwnTabs';

// A full-width page sets its tabs smaller than the side panel does.
const tabClassName = 'size-p2 mb-0 shrink-0 p-3';

interface MachineDetailCompactProps {
  machine: Machine;
}

/**
 * The machine detail as a page of its own. A phone, and a tablet in portrait,
 * leave an overlay panel too narrow to read, so the list navigates here. The
 * app bar above names the machine and shows its status.
 */
export function MachineDetailCompact({ machine }: MachineDetailCompactProps) {
  const replacementTabs = useMachineDetailTabs();
  const dataTabs = useMemo(() => machineDataTabs(machine), [machine]);
  const tabs = replacementTabs ?? [
    ...dataTabs.leading,
    ...MACHINE_OWN_TABS,
    ...dataTabs.trailing,
  ];
  const tabOrder: string[] = tabs.map((tab) => tab.value);

  // The open tab lives in the URL, so the way back from a document returns to
  // the Documents tab.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const openTab =
    requestedTab && tabOrder.includes(requestedTab)
      ? requestedTab
      : tabOrder[0];
  const changeTab = (value: string) =>
    setSearchParams({ tab: value }, { replace: true, state: location.state });

  return (
    // The tabs share this blue, so it runs to the bottom of a short tab.
    <div
      className='bg-surface-focus-support flex w-full min-w-0 flex-col
        [--info-gutter:1rem] md:[--info-gutter:1.5rem]'
    >
      {/* The app bar names the machine. This strip carries its ID and site. */}
      <div
        className='border-border-secondary bg-surface-primary flex items-center
          gap-4 border-b px-6 py-3'
      >
        <div
          className='bg-surface-focus-support flex size-14 shrink-0 items-center
            justify-center rounded-md p-2'
        >
          <img
            src={MACHINE_TILE_IMAGE}
            alt=''
            className='h-full w-full object-contain'
            decoding='async'
          />
        </div>
        <div className='text-secondary min-w-0 flex-1'>
          <p className='size-p2 truncate font-bold'>{machine.machineId}</p>
          {machineSubtitle(machine) && (
            <p className='size-caption truncate'>{machineSubtitle(machine)}</p>
          )}
        </div>
      </div>

      <SidePanelTabs
        value={openTab}
        onValueChange={changeTab}
        enableSwipe
        tabOrder={tabOrder}
      >
        {/* Sticky, so the tabs stay put while the content scrolls. The row
            scrolls sideways, which clips anything past its edge, so its grey
            line is an inset shadow under the tabs. */}
        <SidePanelTabList
          className='bg-surface-primary sticky top-0 z-10 gap-2 overflow-x-auto
            border-b-0 px-4
            shadow-[inset_0_-1px_0_var(--color-border-secondary),0_4px_24px_rgba(0,0,0,0.03)]
            [scrollbar-width:none]'
        >
          {tabs.map((tab) => (
            <SidePanelTab
              key={tab.value}
              value={tab.value}
              className={tabClassName}
            >
              {tab.label}
            </SidePanelTab>
          ))}
        </SidePanelTabList>

        {tabs.map((tab) => (
          <SidePanelTabContent key={tab.value} value={tab.value}>
            {tab.render(machine)}
          </SidePanelTabContent>
        ))}
      </SidePanelTabs>
    </div>
  );
}
